import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Check from '@material-ui/icons/Check';
import regularFormsStyle from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardIcon from "components/Card/CardIcon.jsx";
import Button from "components/CustomButtons/Button.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import "react-table/react-table.css";
import Toast from 'views/Toast/Toast';
import { CallApi } from 'utils/Request';
import LoadingModal from 'components/Loading/Loading';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import PaymentIcon from '@material-ui/icons/Payment';
import CustomCheckbox from 'components/CustomSelect/CustomCheckbox';
import { TransReactTable } from 'components/CustomReactTable/TransReactTable';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => {
    return {
        ...regularFormsStyle,
        descriptionTextArea: {
            width: "100%",
            height: 150
        },
        actionBtn: {
            marginRight: '1rem'
        },
        bulkActions: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px',
            flexWrap: 'wrap'
        },
        selectAllCheckbox: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            marginRight: '10px'
        },
        selectedCount: {
            fontSize: '14px',
            color: '#999',
            marginLeft: '10px'
        },
        rowCheckbox: {
            padding: '0 !important'
        }
    };
})


export default function CostumerListPage(props) {
    const classes = useStyles()
    const [isLoading, setIsLoading] = useState(false)
    const { t } = useTranslation()
    const [costumerList, setCostumerList] = useState([])
    const [selectedIds, setSelectedIds] = useState(new Set())

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        setIsLoading(true)
        setSelectedIds(new Set())
        CallApi({
            url: 'user/costumers',
            method: 'get',
        }).then((res) => {
            if (res.success) {
                setCostumerList(res.data)
            } else {
                Toast(400, res.error);
            }
        }).finally(() => {
            setIsLoading(false)
        })
    }

    // --- Selection handlers ---
    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }
            return next
        })
    }

    const toggleSelectAll = () => {
        if (selectedIds.size === costumerList.length) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(costumerList.map(c => c.id)))
        }
    }

    const onBulkDelete = () => {
        if (selectedIds.size === 0) {
            Toast(400, t('No users selected'))
            return
        }
        if (!window.confirm(t('Are you sure to delete') + ` ${selectedIds.size} ` + t('users') + '?')) {
            return
        }
        setIsLoading(true)
        CallApi({
            url: 'user/bulk_delete',
            method: 'post',
            data: { user_ids: Array.from(selectedIds) }
        }).then((res) => {
            if (res.success) {
                Toast(200, `${res.deleted_count} ${t('users deleted')}`)
                loadData()
            } else {
                Toast(400, res.error)
            }
        }).finally(() => {
            setIsLoading(false)
        })
    }

    // --- Existing handlers ---
    const onToggleActivate = (user_id, check) => {
        if (window.confirm(t('Are you sure?'))) {
            updateCostumer({ user_id, active: check })
        }
    }
    const onToggleFreeResponse = (user_id, check) => {
        if (window.confirm(t('Are you sure?'))) {
            updateCostumer({ user_id, free_response: check })
        }
    }
    const onDeleteUser = (user_id) => {
        if (window.confirm(t('Are you sure to delete this user?'))) {
            updateCostumer({ user_id, deleted: true })
        }
    }
    const updateCostumer = (updateData) => {
        setIsLoading(true)
        CallApi({
            url: 'user/update_user',
            method: 'post',
            data: updateData
        }).then((res) => {
            if (res.success) {
                loadData()
            } else {
                Toast(400, res.error);
            }
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const isAllSelected = costumerList.length > 0 && selectedIds.size === costumerList.length

    const costumerListComponent = costumerList.map((costumer, key) => {
        return {
            ...costumer,
            checkbox: <div style={{ textAlign: 'center' }}>
                <Checkbox
                    checked={selectedIds.has(costumer.id)}
                    onChange={() => toggleSelect(costumer.id)}
                    checkedIcon={<Check style={{ color: '#00acc1' }} />}
                    className={classes.rowCheckbox}
                />
            </div>,
            activate_action: <div>
                <CustomCheckbox
                    checked={costumer.active}
                    onChange={(check) => onToggleActivate(costumer.id, check)}
                    label={costumer.active ? t('Active') : t('Deactive')}
                />
            </div>,
            set_free_response_action: <div>
                <CustomCheckbox
                    checked={costumer.free_response}
                    onChange={(check) => onToggleFreeResponse(costumer.id, check)}
                    label={costumer.free_response ? t('Active') : t('Deactive')}
                />
            </div>,
            actions: <div>
                <Button color='info' justIcon className={classes.actionBtn}
                    onClick={() => {
                        props.history.push('/admin/costumer_update/' + costumer.id)
                    }}
                ><EditIcon /></Button>
                <Button color='danger' justIcon className={classes.actionBtn}
                    onClick={() => onDeleteUser(costumer.id)}>
                    <DeleteIcon />
                </Button>
                <Button color='warning' justIcon className={classes.actionBtn}
                    onClick={() => {
                        props.history.push('/admin/costumer_payments/' + costumer.id)
                    }}
                ><PaymentIcon /></Button>
            </div>
        }
    })

    return (
        <div>
            <GridContainer>
                <GridItem xs={12} sm={12}>
                    <Card>
                        <CardHeader color="success" icon>
                            <CardIcon color="success">
                                <AccountCircleIcon />
                            </CardIcon>
                            <h4 className={classes.cardIconTitle}>Customer</h4>
                        </CardHeader>
                        <CardBody>
                            <div className={classes.bulkActions}>
                                <Button color="info" onClick={() => {
                                    props.history.push('/admin/costumer_create/')
                                }}>{t('Create')}</Button>

                                <div className={classes.selectAllCheckbox} onClick={toggleSelectAll}>
                                    <Checkbox
                                        checked={isAllSelected}
                                        checkedIcon={<Check style={{ color: '#00acc1' }} />}
                                    />
                                    <span>{t('Select All')}</span>
                                </div>

                                {selectedIds.size > 0 && (
                                    <>
                                        <Button color="danger" onClick={onBulkDelete}>
                                            <DeleteIcon /> {t('Delete Selected')} ({selectedIds.size})
                                        </Button>
                                        <span className={classes.selectedCount}>
                                            {selectedIds.size} / {costumerList.length} {t('selected')}
                                        </span>
                                    </>
                                )}
                            </div>
                            <TransReactTable
                                data={costumerListComponent}
                                columns={[
                                    {
                                        Header: "",
                                        accessor: "checkbox",
                                        sortable: false,
                                        filterable: false,
                                        width: 50
                                    },
                                    {
                                        Header: t("First name"),
                                        accessor: "first_name",
                                        width: 150
                                    },
                                    {
                                        Header: t("Last name"),
                                        accessor: "last_name",
                                        width: 150
                                    },
                                    {
                                        Header: t("Company Name"),
                                        accessor: "company_name",
                                        width: 150
                                    },
                                    {
                                        Header: t("Email"),
                                        accessor: "email",
                                        width: 150
                                    },
                                    {
                                        Header: t("Activate"),
                                        accessor: "activate_action",
                                        width: 150
                                    },
                                    {
                                        Header: t("Free Response"),
                                        accessor: "set_free_response_action",
                                        width: 150
                                    },
                                    {
                                        Header: t("Actions"),
                                        accessor: "actions",
                                        sortable: false,
                                        filterable: false
                                    }
                                ]}
                                defaultPageSize={20}
                                className="-striped -highlight"
                            />
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>
            {isLoading && <LoadingModal />}
        </div>
    );
}
